import React, { useState, useEffect } from 'react';
import { signInWithPopup, signInWithRedirect, getRedirectResult, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import './LoginScreen.css';
import { Button } from '@shopify/polaris';
import { getApiBaseUrl } from '../utils/apiClient';
import { verifyFirebaseSession } from '../utils/sessionRestore';
import { isStandaloneDisplayMode, isIosBrowser } from '../hooks/usePwaInstall';

// Popups are unreliable inside iOS home-screen web apps — use redirect sign-in there.
const useRedirectSignIn = isStandaloneDisplayMode() && isIosBrowser();

// Popup failures that a redirect flow can recover from (not user-cancelled)
const POPUP_FALLBACK_CODES = new Set([
  'auth/popup-blocked',
  'auth/operation-not-supported-in-this-environment',
]);

function LoginScreen({ onLogin }) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Surface errors from a redirect sign-in round trip (successful sign-ins
  // are handled by onAuthStateChanged below).
  useEffect(() => {
    if (!useRedirectSignIn) return;
    getRedirectResult(auth).catch((err) => {
      console.error('Redirect sign-in error:', err);
      setError('Google Sign-In failed.');
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user || cancelled) return;
      setLoading(true);
      try {
        const verifyResult = await verifyFirebaseSession(user, getApiBaseUrl());
        if (cancelled) return;
        if (verifyResult.ok) {
          onLogin();
        } else if (useRedirectSignIn) {
          // Redirect sign-in has no popup catch block — surface denial here
          setError(verifyResult.error === 'Unauthorized email'
            ? `Access denied. ${user.email} is not authorized.`
            : 'Backend verification failed.');
          await signOut(auth);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [onLogin]);

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      if (useRedirectSignIn) {
        // Page navigates away; onAuthStateChanged completes login on return.
        await signInWithRedirect(auth, googleProvider);
        return;
      }

      let result;
      try {
        result = await signInWithPopup(auth, googleProvider);
      } catch (popupErr) {
        if (isIosBrowser() && POPUP_FALLBACK_CODES.has(popupErr.code)) {
          await signInWithRedirect(auth, googleProvider);
          return;
        }
        throw popupErr;
      }

      const verifyResult = await verifyFirebaseSession(result.user, getApiBaseUrl());
      if (verifyResult.ok) {
        onLogin();
      } else {
        setError(verifyResult.error === 'Unauthorized email'
          ? `Access denied. ${result.user.email} is not authorized.`
          : 'Backend verification failed.');
        await signOut(auth);
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/configuration-not-found') {
        setError('Google Sign-In is not enabled in Firebase Console.');
      } else {
        setError('Google Sign-In failed.');
      }
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  return (
    <div className="login-screen">
      <div className="login-container">
        <div className="login-header">
          <h1>🔒 Inventory Manager</h1>
          <p>Secure Access Required</p>
        </div>

        <div className="login-form" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
          {error && (
            <div className="error-message">
              <span>⚠️ {error}</span>
            </div>
          )}

          <div style={{ marginTop: '20px' }}>
            <Button
              size="large"
              variant="primary"
              onClick={handleGoogleLogin}
              loading={loading}
            >
              Sign in with Google
            </Button>
          </div>

          <div style={{ marginTop: '10px', paddingTop: '20px', borderTop: '1px solid #e0e0e0', width: '100%', textAlign: 'center' }}>
            <p style={{ marginBottom: '10px', color: '#666', fontSize: '14px' }}>Front Reception</p>
            <Button
              size="large"
              url="/job"
            >
              📋 Create Job Intake
            </Button>
          </div>
        </div>

        <div className="login-footer">
          <p>Version {process.env.REACT_APP_VERSION || '3.0.0'} • Google Secure Auth</p>
        </div>
      </div>
    </div>
  );
}

export default LoginScreen;
